import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, ListTodo, Reply, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface EmailMessage {
  id: string;
  provider: 'microsoft' | 'google';
  subject: string;
  from: string;
  from_email: string;
  preview: string;
  date: string;
  is_read: boolean;
  has_attachments: boolean;
  account: string;
}

interface Props {
  email: EmailMessage;
}

export function EmailReadingPanel({ email }: Props) {
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiActions, setAiActions] = useState<string | null>(null);
  const [aiReplies, setAiReplies] = useState<string | null>(null);

  const summarize = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('email-ai', {
        body: { action: 'summary', email_content: email.preview },
      });
      if (error) throw error;
      return data.result as string;
    },
    onSuccess: (result) => setAiSummary(result),
  });

  const detectActions = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('email-ai', {
        body: { action: 'actions', email_content: email.preview },
      });
      if (error) throw error;
      return data.result as string;
    },
    onSuccess: (result) => setAiActions(result),
  });

  const suggestReplies = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('email-ai', {
        body: { action: 'suggest_replies', email_content: email.preview },
      });
      if (error) throw error;
      return data.result as string;
    },
    onSuccess: (result) => setAiReplies(result),
  });

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold">{email.subject}</h2>
        <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{email.from}</span>
          <span>&lt;{email.from_email}&gt;</span>
          <span>·</span>
          <span>{new Date(email.date).toLocaleString()}</span>
        </div>
      </div>

      <Separator />

      {/* Email Content */}
      <div className="prose prose-sm max-w-none">
        <p>{email.preview}</p>
      </div>

      <Separator />

      {/* AI Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => summarize.mutate()}
          disabled={summarize.isPending}
        >
          {summarize.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          AI Summary
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => detectActions.mutate()}
          disabled={detectActions.isPending}
        >
          {detectActions.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListTodo className="mr-2 h-4 w-4" />}
          Detect Actions
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => suggestReplies.mutate()}
          disabled={suggestReplies.isPending}
        >
          {suggestReplies.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Reply className="mr-2 h-4 w-4" />}
          Suggest Replies
        </Button>
      </div>

      {/* AI Results */}
      {aiSummary && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> AI Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{aiSummary}</p>
          </CardContent>
        </Card>
      )}

      {aiActions && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ListTodo className="h-4 w-4 text-primary" /> Detected Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{aiActions}</p>
          </CardContent>
        </Card>
      )}

      {aiReplies && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Reply className="h-4 w-4 text-primary" /> Suggested Replies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{aiReplies}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
